import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { authService } from '../services/api';
import '../styles/AuthPage.css';

const SignupPage = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password1: '',
    password2: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const key = searchParams.get('key');

  useEffect(() => {
    if (key) {
      verifyEmail(key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const verifyEmail = async (verificationKey) => {
    setIsLoading(true);
    try {
      await authService.verifyEmail(verificationKey);
      setVerificationMessage('Email verified successfully! You can now log in.');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      setErrors({
        verification: error.response?.data?.detail || 'Email verification failed',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password1) {
      newErrors.password1 = 'Password is required';
    } else if (formData.password1.length < 8) {
      newErrors.password1 = 'Password must be at least 8 characters';
    }

    if (!formData.password2) {
      newErrors.password2 = 'Please confirm your password';
    } else if (formData.password1 !== formData.password2) {
      newErrors.password2 = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await authService.signup(formData);
      setSignupSuccess(true);
    } catch (error) {
      console.error('Signup error:', error);
      const errorData = error.response?.data || {};

      if (errorData.username) {
        setErrors((prev) => ({
          ...prev,
          username: errorData.username[0] || errorData.username,
        }));
      }
      if (errorData.email) {
        setErrors((prev) => ({
          ...prev,
          email: errorData.email[0] || errorData.email,
        }));
      }
      if (errorData.password1) {
        setErrors((prev) => ({
          ...prev,
          password1: errorData.password1[0] || errorData.password1,
        }));
      }
      if (errorData.non_field_errors) {
        setErrors((prev) => ({
          ...prev,
          form: errorData.non_field_errors[0] || errorData.non_field_errors,
        }));
      }
      if (errorData.detail) {
        setErrors((prev) => ({ ...prev, form: errorData.detail }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (key) {
    return (
      <div className="auth-page">
        <div className="card">
          <div className="auth-header">
            <h1>Verifying email</h1>
            {isLoading && <p>Please wait…</p>}
          </div>
          {verificationMessage && (
            <div className="form-banner success">{verificationMessage}</div>
          )}
          {errors.verification && (
            <div className="form-banner" role="alert">
              {errors.verification}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (signupSuccess) {
    return (
      <div className="auth-page">
        <div className="card">
          <div className="auth-header">
            <h1>Check your email</h1>
            <p>Your account was created. We sent a verification link to your email.</p>
          </div>
          <div className="form-banner success">
            Click the link in the email to verify your account, then sign in.
          </div>
          <div className="auth-foot">
            <p>
              <Link to="/login">Go to sign in</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="card">
        <div className="auth-header">
          <h1>Create your account</h1>
          <p>Join Strat Guys and start planning your strategies</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {errors.form && (
            <div className="form-banner" role="alert">
              {errors.form}
            </div>
          )}

          <div>
            <label className="form-label" htmlFor="username">
              Username
            </label>
            <input
              className={`form-input${errors.username ? ' error' : ''}`}
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Choose a username"
              required
              autoComplete="username"
            />
            {errors.username && <span className="field-error">{errors.username}</span>}
          </div>

          <div>
            <label className="form-label" htmlFor="email">
              Email
            </label>
            <input
              className={`form-input${errors.email ? ' error' : ''}`}
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              required
              autoComplete="email"
            />
            {errors.email && <span className="field-error">{errors.email}</span>}
          </div>

          <div>
            <label className="form-label" htmlFor="password1">
              Password
            </label>
            <input
              className={`form-input${errors.password1 ? ' error' : ''}`}
              type="password"
              id="password1"
              name="password1"
              value={formData.password1}
              onChange={handleChange}
              placeholder="Create a password"
              required
              autoComplete="new-password"
            />
            {errors.password1 && <span className="field-error">{errors.password1}</span>}
          </div>

          <div>
            <label className="form-label" htmlFor="password2">
              Confirm Password
            </label>
            <input
              className={`form-input${errors.password2 ? ' error' : ''}`}
              type="password"
              id="password2"
              name="password2"
              value={formData.password2}
              onChange={handleChange}
              placeholder="Confirm your password"
              required
              autoComplete="new-password"
            />
            {errors.password2 && <span className="field-error">{errors.password2}</span>}
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}
            aria-busy={isLoading}
          >
            {isLoading ? 'Creating Account...' : 'Sign Up'}
          </button>
        </form>

        <div className="auth-foot">
          <p>
            Already have an account? <Link to="/login">Login</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
